import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchBillingSubscription,
  type BillingSubscriptionSummary,
} from '../services/api';
import { getFirebaseAuth } from '../config/firebase';
import { useUserRecovery } from './useUserRecovery';
import { type RecoveryEmailState } from './userDocStore';

export type { RecoveryEmailState };

type Cache = {
  uid: string | null;
  summary: BillingSubscriptionSummary | null;
  loadedAt: number;
};

let memoryCache: Cache | null = null;
/** De-dupes the callable across concurrent mounts of this hook. */
let inFlight: { uid: string | null; promise: Promise<BillingSubscriptionSummary> } | null =
  null;

function cacheForUid(uid: string | null): Cache | null {
  if (!memoryCache || memoryCache.uid !== uid) return null;
  return memoryCache;
}

/**
 * Billing state for the screens that show a plan. Terms / recovery email come
 * from the shared `users/{uid}` listener, so mounting this hook is the only
 * thing that costs a `getBillingSubscription` round trip — the auth gates read
 * the document directly through `useUserRecovery`.
 */
export function useBillingSummary(enabled: boolean) {
  const uid = enabled ? (getFirebaseAuth().currentUser?.uid ?? null) : null;
  const cached = cacheForUid(uid);
  const { recovery, recoveryReady } = useUserRecovery(enabled);

  const [summary, setSummary] = useState<BillingSubscriptionSummary | null>(
    cached?.summary ?? null,
  );
  const [loading, setLoading] = useState(!cached?.summary && enabled);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const refresh = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled) return;
    const currentUid = getFirebaseAuth().currentUser?.uid ?? null;
    const hit = cacheForUid(currentUid);
    if (!opts?.force && hit?.summary && Date.now() - hit.loadedAt < 30_000) {
      setSummary(hit.summary);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Two screens mounting at once (portal shell + dashboard) asked the
      // backend the same question twice; share the flight instead.
      if (!opts?.force && inFlight && inFlight.uid === currentUid) {
        setSummary(await inFlight.promise);
        return;
      }
      const flight = fetchBillingSubscription();
      inFlight = { uid: currentUid, promise: flight };
      try {
        const data = await flight;
        memoryCache = { uid: currentUid, summary: data, loadedAt: Date.now() };
        setSummary(data);
      } finally {
        if (inFlight?.promise === flight) inFlight = null;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao carregar plano');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchedRef.current = false;
  }, [enabled, uid]);

  useEffect(() => {
    if (!enabled) return;
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      void refresh();
    }
  }, [enabled, refresh, uid]);

  const invalidate = useCallback(() => {
    memoryCache = null;
    inFlight = null;
    fetchedRef.current = false;
  }, []);

  return {
    summary,
    recovery,
    recoveryReady,
    loading,
    error,
    refresh,
    invalidate,
  };
}

/**
 * Only the billing answer is cached, so only it needs dropping after a
 * payment. The `users/{uid}` document is live — nothing to invalidate.
 */
export function invalidateBillingSummaryCache() {
  memoryCache = null;
  inFlight = null;
}
